import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { CompanyGroupResponse, ClientResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper } from '../utils';
import { ICONS } from '../icons';
import { CompanyGroupDetailsSectionHandler } from './details.section';
import { RegisterEntityHandler } from '../../entities';
import { AppFeatures, AppFeatureFlagDefaults, APP_FEATURE_FLAGS } from 'app/app.features';
import { FeatureFlagsSectionHandler } from '../shared/app-features.section';
import { getBackendFeatureDefaults } from '../client/backend.features';
import { EmailTemplateSectionHandler } from './emailtemplate.section';
import { SoftLimitSectionHandler } from '../shared/limits.section';
import { RetentionSectionHandler } from '../shared/retention.section';
import { PasswordPolicySectionHandler } from '../shared/password-policy.section';

@RegisterEntityHandler('companygroup')
@Injectable()
export class CompanyGroupHandler extends EntityHandler<CompanyGroupResponse> {

    constructor(
        admin: AdminEditorService
    ) {
        super(admin);
        this.listMode = 'tree';
    }

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listCompanyGroups/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.accounts.listCompanyGroups(ownerId), 3).then(response => ({
            ...response,
            type: 'companygroup',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.COMPANYGROUPS'),
        }));
    }

    getEntity(id: string): Promise<CompanyGroupResponse> {
        return this.admin.getCached(`getCompanyGroup/${id}`, api => api.accounts.getCompanyGroup(id), 10);
    }

    createEntity(ownerId: string, entity: any): Promise<CompanyGroupResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.accounts.createCompanyGroup(entity));
    }


    setEntityState(entity: any, state: string): Promise<any> {
        return state === 'deleted' ? this.admin.applyUpdate(api => api.accounts.deleteCompanyGroup(entity.id)) : Promise.reject('Only deletion supported');
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.COMPANYGROUP.COLUMNS');
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', (row) => {
                return row.folder ? ICONS.folder : ICONS.companygroup;
            }),
            grid.TimeAgoColumn('entity.modifiedDate', 'MODIFIED'),
        ];
        return Promise.resolve(columns);
    }

    getEditorSections(ownerId: string, entity: CompanyGroupResponse): Promise<SectionHandlerGroup<CompanyGroupResponse>[]> {
        const sections: SectionHandlerGroup<ClientResponse>[] = [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new CompanyGroupDetailsSectionHandler(this.admin, ownerId),
                this.admin.user.owner.type === 'system' ? new PasswordPolicySectionHandler<CompanyGroupResponse>(this.admin, ownerId, 'companygroup') : null, 
            ].filter(x => x),
        }];
        if (entity.owner.type === 'vendor') { // this is a client group
            sections.push({
                name: this.admin.i18n.instant('ADMIN.EDITOR.APP_FEATURES'),
                sections: Object.keys(AppFeatures).map(page => new FeatureFlagsSectionHandler<ClientResponse>(this.admin, ownerId, AppFeatures[page], AppFeatureFlagDefaults, APP_FEATURE_FLAGS,
                    (e, payload) => {
                        return this.admin.applyUpdate(api => api.accounts.updateCompanyGroup(e.id, payload));
                    })),
            });
            const backend = getBackendFeatureDefaults(this.admin.user.owner.type);
            sections.push({
                name: this.admin.i18n.instant('ADMIN.EDITOR.BACKEND_FEATURES'),
                sections: [
                    ...Object.keys(backend.sections).map(section => new FeatureFlagsSectionHandler<ClientResponse>(this.admin, ownerId, backend.sections[section], backend.defaults, backend.bucket,
                    (e, payload) => {
                        return this.admin.applyUpdate(api => api.accounts.updateCompanyGroup(e.id, payload));
                    })),
                    this.admin.user.owner.type === 'system' ? new SoftLimitSectionHandler<CompanyGroupResponse>(this.admin, ownerId, 'companygroup') : null, // limits are only available to system users
                    this.admin.user.owner.type === 'system' ? new RetentionSectionHandler<CompanyGroupResponse>(this.admin, ownerId, 'companygroup') : null,
                ].filter(x => x),
            });
            sections.push({
                name: this.admin.i18n.instant('ADMIN.EDITOR.EMAIL_CUSTOMIZATION'),
                sections: [
                    new EmailTemplateSectionHandler(this.admin, ownerId, 'user_invite'),
                    new EmailTemplateSectionHandler(this.admin, ownerId, 'user_reset'),
                    new EmailTemplateSectionHandler(this.admin, ownerId, 'forgot_password'),
                ],
            });
        }
        return Promise.resolve(sections);
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: any, sections: FormSectionHandler<CompanyGroupResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'companygroup',
            entity: {
                parent: null,
            },
            sections: [
                new CompanyGroupDetailsSectionHandler(this.admin, ownerId),
            ],
        });
    }

}













